import { messageErreur } from '@imp/shared/message-erreur';
import { nomDocumentImprime } from '@imp/shared/nom-document';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useTitreDocument } from '@/lib/titre';
import { contactDisplayName } from '@/modules/clients/api';
import { type DevisPdfContact } from '@/modules/devis/api';
import { getFactureForPdf } from '@/modules/factures/api';
import { getOrganisation } from '@/modules/settings/api';

function fmtEuro(v: string | number | null): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number(v ?? 0),
  );
}
function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}
function fmtPct(taux: string | null): string {
  if (taux === null) return '';
  return `${(Number(taux) * 100).toLocaleString('fr-FR')} %`;
}
function filled(v: string | null | undefined): string | null {
  const t = (v ?? '').trim();
  return t === '' || t.toUpperCase() === 'À COMPLÉTER' ? null : t;
}
function clientNom(c: DevisPdfContact): string {
  if (c.type_personne === 'pro') return c.raison_sociale ?? '';
  return [c.civilite, c.prenom, c.nom].filter(Boolean).join(' ');
}
function ligneTtc(totalHt: string | number | null, taux: string | null): number {
  const ht = Number(totalHt ?? 0);
  return taux === null ? ht : ht * (1 + Number(taux));
}
// Assombrit une couleur hex (les bleus profonds de l'en-tête/pied suivent la
// couleur secondaire choisie dans Paramètres, même personnalisée).
function darken(hex: string, f: number): string {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.replace(/(.)/g, '$1$1') : m;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return hex;
  const ch = (shift: number) =>
    Math.round(((n >> shift) & 255) * f)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(16)}${ch(8)}${ch(0)}`;
}

const PRINT_CSS = `
@page { size: A4; margin: 0; }
@media print {
  html, body { background: #fff !important; }
  .pdf-toolbar { display: none !important; }
  .pdf-page { box-shadow: none !important; margin: 0 !important; max-width: none !important; min-height: 297mm; }
}
`;

export function FacturePdfPage() {
  const { id } = useParams();
  const factureQ = useQuery({
    queryKey: ['facture-pdf', id],
    queryFn: () => getFactureForPdf(id ?? ''),
    enabled: Boolean(id),
  });
  const orgQ = useQuery({ queryKey: ['organisation'], queryFn: getOrganisation });

  const f = factureQ.data;
  const org = orgQ.data;

  // Un avoir ne doit pas s'enregistrer sous le nom « Facture ». Et un brouillon
  // porte un numéro technique (BROUILLON-3f2a…) qui n'a rien à faire dans un
  // nom de fichier : on l'annonce comme brouillon, sans numéro.
  const brouillonPdf = f?.statut === 'brouillon';
  // Une facture d'acompte et une facture de solde ne s'appellent pas
  // « facture » tout court : le client doit savoir ce qu'il tient.
  const typePiece =
    f?.kind === 'avoir'
      ? 'Avoir'
      : f?.kind === 'acompte'
        ? "Facture d'acompte"
        : f?.kind === 'finale'
          ? 'Facture de solde'
          : 'Facture';
  useTitreDocument(
    f
      ? nomDocumentImprime(
          brouillonPdf ? `${typePiece} brouillon` : typePiece,
          brouillonPdf ? null : f.numero,
          f.contacts && contactDisplayName(f.contacts),
        )
      : null,
  );
  const PRIMARY =
    org?.couleur_primaire && org.couleur_primaire !== '#1e40af' ? org.couleur_primaire : '#E1501A';
  const SECONDARY =
    org?.couleur_secondaire && org.couleur_secondaire !== '#64748b'
      ? org.couleur_secondaire
      : '#1D4595';
  const NAVY = darken(SECONDARY, 0.62);
  const NAVY_MID = darken(SECONDARY, 0.78);

  if (factureQ.isLoading || orgQ.isLoading) {
    return <p className="text-muted-foreground p-8">Chargement de la facture…</p>;
  }
  if (factureQ.isError || !f) {
    return (
      <p className="text-destructive p-8">
        Erreur : {factureQ.error ? messageErreur(factureQ.error) : 'facture introuvable'}
      </p>
    );
  }

  const contact = f.contacts;
  // Adresse de facturation : snapshot figé à l'émission, sinon adresse du contact.
  const cache = f.facturation_adresse;
  const adrContact = (contact?.adresses ?? []).find(
    (a) => a.usage === 'facturation' || a.est_default,
  );
  const billing = cache
    ? {
        ligne1: cache.ligne1,
        ligne2: cache.ligne2 ?? null,
        cp: cache.codePostal,
        ville: cache.ville,
      }
    : adrContact
      ? {
          ligne1: adrContact.ligne1,
          ligne2: adrContact.ligne2,
          cp: adrContact.code_postal,
          ville: adrContact.ville,
        }
      : null;

  // Une facture émise porte l'adresse figée à l'émission ; pour un brouillon,
  // c'est encore celle du client. Dans les deux cas, son absence se voit ici
  // plutôt que sur le PDF envoyé au client.
  const sansAdresse = billing === null;
  const detailTva = f.detail_tva ?? [];
  const paye = Number(f.montant_paye);
  const reste = Number(f.montant_ttc) - paye;
  const brouillon = f.statut === 'brouillon';
  const avoir = f.kind === 'avoir';
  const avoirDe = f.metadata?.avoir_de ?? null;

  const forme = filled(org?.forme_juridique);
  const capital = filled(org?.capital_social);
  const naf = filled(org?.naf);
  const rcs = filled(org?.rcs);
  const tvaIntra = filled(org?.tva_intracom);
  const siteWeb = filled(org?.site_web);
  const bic = filled(org?.bic);
  const decPolice = filled(org?.decennale_numero_police);
  const legalLine = [
    forme && capital ? `${forme} au capital de ${capital} €` : forme,
    naf ? `APE ${naf}` : null,
    `SIRET ${org?.siret_siege ?? ''}`,
    rcs ? `RCS ${rcs}` : null,
    tvaIntra ? `TVA ${tvaIntra}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const sectionTitle = (txt: string) => (
    <div
      className="mb-1.5 text-[10px] font-bold uppercase"
      style={{ color: SECONDARY, letterSpacing: '0.13em' }}
    >
      {txt}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-6 print:bg-white print:py-0">
      <style>{PRINT_CSS}</style>

      <div className="pdf-toolbar mx-auto mb-4 flex max-w-[210mm] items-center justify-between px-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/factures/${id ?? ''}`}>
            <ArrowLeft className="h-4 w-4" /> Retour à la facture
          </Link>
        </Button>
        <Button
          size="sm"
          onClick={() => {
            window.print();
          }}
        >
          <Printer className="h-4 w-4" /> Imprimer / Enregistrer en PDF
        </Button>
      </div>

      {sansAdresse ? (
        <div className="pdf-toolbar mx-auto mb-4 max-w-[210mm] rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <strong>Ce client n&apos;a pas d&apos;adresse postale.</strong> Une facture doit porter
          l&apos;adresse du client : rien ne s&apos;imprimera sous son nom.{' '}
          <Link to={`/clients/${f.contact_id}`} className="underline">
            Renseigner son adresse
          </Link>
          {brouillon
            ? ', puis revenir ici — après émission, l’adresse est figée.'
            : '. Attention : cette facture est déjà émise, son adresse restera celle figée à l’émission.'}
        </div>
      ) : null}

      <div
        className="pdf-page mx-auto flex max-w-[210mm] flex-col bg-white shadow-lg"
        style={{
          color: '#242B3A',
          fontSize: '12.5px',
          lineHeight: 1.5,
          minHeight: '297mm',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        {/* Bandeau société */}
        <header
          className="flex items-center justify-between gap-8 px-[12mm] py-[8mm] text-white"
          style={{
            background: `linear-gradient(118deg, ${SECONDARY} 0%, ${NAVY_MID} 55%, ${NAVY} 100%)`,
          }}
        >
          <div className="flex items-center gap-3.5">
            {filled(org?.logo_url) ? (
              <div className="rounded bg-white p-1.5">
                <img src={org?.logo_url ?? ''} alt="" className="h-12 object-contain" />
              </div>
            ) : (
              <svg width="46" height="38" viewBox="0 0 42 36" fill="none" aria-hidden="true">
                <path
                  d="M3 21 L21 6 L39 21"
                  stroke="#FFFFFF"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M30.5 6.5 V13" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" />
                <path
                  d="M10.5 27 L21 18.2 L31.5 27"
                  stroke="#8FABE3"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <div>
              <div className="text-[26px] font-extrabold leading-none tracking-wide">
                {org?.raison_sociale ?? 'IMP TOITURE'}
              </div>
              {siteWeb ? (
                <div
                  className="mt-1.5 text-[9.5px] font-semibold uppercase"
                  style={{ color: '#9DB7E8', letterSpacing: '0.22em' }}
                >
                  {siteWeb}
                </div>
              ) : null}
            </div>
          </div>
          <div className="text-right text-[10.5px] leading-[1.8]" style={{ color: '#A9C2EF' }}>
            {filled(org?.adresse_ligne1) ? <div>{org?.adresse_ligne1}</div> : null}
            {filled(org?.adresse_ligne2) ? <div>{org?.adresse_ligne2}</div> : null}
            <div>
              {org?.code_postal} {org?.ville}
            </div>
            {filled(org?.telephone) ? <div>Tél. {org?.telephone}</div> : null}
            {filled(org?.email) ? <div style={{ color: '#E3EBFA' }}>{org?.email}</div> : null}
          </div>
        </header>

        {/* Liseré ligne de toit */}
        <svg className="block w-full" height="14" aria-hidden="true">
          <path
            d="M0 11.75 H53 L71 3 L89 11.75 H3000"
            fill="none"
            stroke={PRIMARY}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </svg>

        {/* Corps */}
        <div className="flex-1 px-[12mm] pb-[6mm] pt-[5mm]">
          {/* Titre + numéro + dates */}
          <div className="flex items-end justify-between gap-6">
            <div className="flex items-center gap-3.5">
              <div
                className="text-[28px] font-extrabold leading-none"
                style={{ color: NAVY, letterSpacing: '0.05em' }}
              >
                {typePiece.toUpperCase()}
              </div>
              {brouillon ? (
                <div
                  className="rounded-full px-3 py-1 text-[10px] font-bold uppercase"
                  style={{ backgroundColor: '#F1F5FB', color: SECONDARY }}
                >
                  Brouillon
                </div>
              ) : (
                <div
                  className="whitespace-nowrap rounded-full border-[1.5px] px-3.5 py-1 text-[12px] font-bold"
                  style={{ borderColor: '#BCCDEC', color: SECONDARY }}
                >
                  N° {f.numero}
                </div>
              )}
            </div>
            <div className="text-right text-[11.5px] leading-[1.7]">
              <div>
                <span className="text-gray-500">Émise le : </span>
                <span className="font-semibold tabular-nums">{fmtDate(f.date_emission)}</span>
              </div>
              {f.date_prestation ? (
                <div>
                  <span className="text-gray-500">Prestation du : </span>
                  <span className="font-semibold tabular-nums">{fmtDate(f.date_prestation)}</span>
                </div>
              ) : null}
              {avoir ? (
                <div className="font-bold" style={{ color: PRIMARY }}>
                  Annule la facture n° {avoirDe?.numero ?? '—'}
                  {avoirDe ? ` du ${fmtDate(avoirDe.date_emission)}` : ''}
                </div>
              ) : (
                <div className="font-bold" style={{ color: PRIMARY }}>
                  Échéance : <span className="tabular-nums">{fmtDate(f.date_echeance)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Facturé à (à droite : convention enveloppe à fenêtre) */}
          <section className="mt-[5mm] grid grid-cols-2 gap-[5mm]">
            <div />
            <div
              className="rounded-[5px] border px-3 py-2.5"
              style={{ borderColor: '#DFE7F3', borderTop: `3px solid ${SECONDARY}` }}
            >
              {sectionTitle('Facturé à')}
              {contact ? (
                <div className="text-[12px] leading-[1.55]">
                  <div className="text-[13px] font-bold" style={{ color: NAVY }}>
                    {clientNom(contact)}
                  </div>
                  {billing ? (
                    <>
                      <div>{billing.ligne1}</div>
                      {billing.ligne2 ? <div>{billing.ligne2}</div> : null}
                      <div>
                        {billing.cp} {billing.ville}
                      </div>
                    </>
                  ) : null}
                  {contact.type_personne === 'pro' && filled(contact.siret) ? (
                    <div className="text-[11px] text-gray-500">SIRET : {contact.siret}</div>
                  ) : null}
                </div>
              ) : (
                <div className="text-gray-500">—</div>
              )}
            </div>
          </section>

          {/* Objet */}
          <div
            className="mt-[5mm] rounded-r-[5px] px-3.5 py-2 text-[12.5px]"
            style={{ backgroundColor: '#F1F5FB', borderLeft: `3px solid ${SECONDARY}` }}
          >
            <span className="font-bold" style={{ color: SECONDARY }}>
              Objet :{' '}
            </span>
            <span className="font-semibold">{f.objet}</span>
          </div>

          {/* Lignes */}
          <table className="mt-[5mm] w-full border-collapse text-[12px]">
            <thead>
              <tr className="text-left text-white" style={{ backgroundColor: SECONDARY }}>
                <th className="px-2.5 py-2 text-[10px] font-bold uppercase tracking-wider">
                  Description
                </th>
                <th className="px-2.5 py-2 text-right text-[10px] font-bold uppercase tracking-wider">
                  Qté
                </th>
                <th className="px-2.5 py-2 text-center text-[10px] font-bold uppercase tracking-wider">
                  Unité
                </th>
                <th className="px-2.5 py-2 text-right text-[10px] font-bold uppercase tracking-wider">
                  PU HT
                </th>
                <th className="px-2.5 py-2 text-right text-[10px] font-bold uppercase tracking-wider">
                  Montant HT
                </th>
                <th className="px-2.5 py-2 text-right text-[10px] font-bold uppercase tracking-wider">
                  TVA
                </th>
                <th className="px-2.5 py-2 text-right text-[10px] font-bold uppercase tracking-wider">
                  Montant TTC
                </th>
              </tr>
            </thead>
            <tbody>
              {f.factures_lignes.map((l, i) => (
                <tr
                  key={l.id}
                  className="align-top"
                  style={{
                    backgroundColor: i % 2 === 1 ? '#F8FAFD' : undefined,
                    borderBottom: '1px solid #E6EBF4',
                  }}
                >
                  <td className="px-2.5 py-2 font-medium">
                    {l.libelle}
                    {l.description === null || l.description.trim() === '' ? null : (
                      <div
                        className="mt-0.5 whitespace-pre-line text-[10px] font-normal leading-snug"
                        style={{ color: '#5B6472' }}
                      >
                        {l.description}
                      </div>
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums">
                    {Number(l.quantite).toLocaleString('fr-FR')}
                  </td>
                  <td className="px-2.5 py-2 text-center text-gray-500">{l.unites?.code ?? ''}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">
                    {fmtEuro(l.prix_unitaire_ht)}
                  </td>
                  <td className="px-2.5 py-2 text-right font-medium tabular-nums">
                    {fmtEuro(l.total_ht)}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-gray-500">
                    {fmtPct(l.taux_tva?.taux ?? null)}
                  </td>
                  <td className="px-2.5 py-2 text-right font-semibold tabular-nums">
                    {fmtEuro(ligneTtc(l.total_ht, l.taux_tva?.taux ?? null))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Règlement + totaux */}
          <section className="mt-[6mm] flex items-start gap-[7mm]">
            <div className="min-w-0 flex-[1.15]">
              {sectionTitle(avoir ? 'Avoir' : 'Règlement')}
              {avoir ? (
                <div className="text-[11.5px] leading-[1.65] text-gray-700">
                  Cet avoir annule intégralement la facture n° {avoirDe?.numero ?? '—'}. Son montant
                  est à déduire de votre prochaine facture ou vous est remboursé.
                </div>
              ) : (
                <div className="text-[11.5px] leading-[1.65] text-gray-700">
                  À régler avant le{' '}
                  <span className="font-semibold tabular-nums">{fmtDate(f.date_echeance)}</span>{' '}
                  (sous {f.delai_paiement_jours} jours) par virement ou chèque.
                </div>
              )}
              {filled(org?.iban) && !avoir ? (
                <div
                  className="mt-2 rounded border px-3 py-1.5 text-[10.5px] font-semibold tabular-nums"
                  style={{ backgroundColor: '#F4F7FC', borderColor: '#E1E8F4', color: NAVY }}
                >
                  IBAN {org?.iban}
                  {bic ? ` — BIC ${bic}` : ''}
                </div>
              ) : null}
              {avoir ? null : (
                <div className="mt-2 text-[10px] italic leading-snug text-gray-500">
                  En cas de retard de paiement, application de pénalités de retard au taux de{' '}
                  {fmtPct(f.penalites_retard_taux)} et d’une indemnité forfaitaire de{' '}
                  {fmtEuro(f.indemnite_forfaitaire)} pour frais de recouvrement (art. L441-10 et
                  D441-5 du Code de commerce). Pas d’escompte pour paiement anticipé.
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="overflow-hidden rounded-md border" style={{ borderColor: '#DFE7F3' }}>
                <div
                  className="flex items-baseline justify-between px-3.5 py-2 text-[12.5px]"
                  style={{ borderBottom: '1px solid #EDF1F8' }}
                >
                  <span className="text-gray-600">Total HT</span>
                  <span className="font-semibold tabular-nums">{fmtEuro(f.montant_ht)}</span>
                </div>
                <div
                  className="flex items-baseline justify-between px-3.5 py-2 text-[12.5px]"
                  style={{ borderBottom: '1px solid #EDF1F8' }}
                >
                  <span className="text-gray-600">Total TVA</span>
                  <span className="font-semibold tabular-nums">{fmtEuro(f.montant_tva)}</span>
                </div>
                <div
                  className="flex items-center justify-between px-3.5 py-2.5 text-white"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <span className="text-[11px] font-bold uppercase tracking-widest">Total TTC</span>
                  <span className="text-[17px] font-extrabold tabular-nums">
                    {fmtEuro(f.montant_ttc)}
                  </span>
                </div>
                {paye > 0 && !avoir ? (
                  <div
                    className="flex items-baseline justify-between px-3.5 py-2 text-[11.5px] text-gray-600"
                    style={{ borderTop: '1px solid #EDF1F8' }}
                  >
                    <span>Déjà réglé</span>
                    <span className="tabular-nums">{fmtEuro(paye)}</span>
                  </div>
                ) : null}
                <div
                  className="flex items-baseline justify-between px-3.5 py-2 text-[11.5px]"
                  style={{ backgroundColor: '#F8FAFD', borderTop: '1px solid #EDF1F8' }}
                >
                  <span className="text-gray-600">
                    {avoir ? 'Montant de l’avoir' : 'Net à payer'}
                  </span>
                  <span className="font-bold tabular-nums" style={{ color: SECONDARY }}>
                    {fmtEuro(avoir ? f.montant_ttc : reste)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Détail TVA */}
          {detailTva.length > 0 ? (
            <section className="mt-[5mm] flex items-start gap-[7mm]">
              <div className="min-w-0 flex-[1.15]">
                {sectionTitle('Détail de la TVA')}
                <table className="w-full border-collapse text-[11px]">
                  <thead>
                    <tr>
                      <th
                        className="border px-2.5 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider"
                        style={{
                          backgroundColor: '#F1F5FB',
                          borderColor: '#E1E8F4',
                          color: SECONDARY,
                        }}
                      >
                        Taux
                      </th>
                      <th
                        className="border px-2.5 py-1.5 text-right text-[9px] font-bold uppercase tracking-wider"
                        style={{
                          backgroundColor: '#F1F5FB',
                          borderColor: '#E1E8F4',
                          color: SECONDARY,
                        }}
                      >
                        Base HT
                      </th>
                      <th
                        className="border px-2.5 py-1.5 text-right text-[9px] font-bold uppercase tracking-wider"
                        style={{
                          backgroundColor: '#F1F5FB',
                          borderColor: '#E1E8F4',
                          color: SECONDARY,
                        }}
                      >
                        Montant TVA
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailTva.map((t) => (
                      <tr key={t.tauxCode}>
                        <td
                          className="border px-2.5 py-1.5 text-gray-700"
                          style={{ borderColor: '#E1E8F4' }}
                        >
                          {(t.tauxValeur * 100).toLocaleString('fr-FR')} %
                        </td>
                        <td
                          className="border px-2.5 py-1.5 text-right tabular-nums text-gray-700"
                          style={{ borderColor: '#E1E8F4' }}
                        >
                          {fmtEuro(t.baseHt)}
                        </td>
                        <td
                          className="border px-2.5 py-1.5 text-right tabular-nums text-gray-700"
                          style={{ borderColor: '#E1E8F4' }}
                        >
                          {fmtEuro(t.montantTva)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex-1" />
            </section>
          ) : null}
        </div>

        {/* Pied légal */}
        <footer
          className="px-[12mm] py-3 text-center leading-[1.75]"
          style={{ backgroundColor: NAVY, borderTop: `3px solid ${SECONDARY}` }}
        >
          <div className="text-[9.5px] font-semibold" style={{ color: '#E6EDFA' }}>
            {legalLine}
          </div>
          {filled(org?.decennale_compagnie) ? (
            <div className="text-[9.5px]" style={{ color: '#9FB6E4' }}>
              Assurance décennale : {org?.decennale_compagnie}
              {decPolice ? ` — police n° ${decPolice}` : ''}
            </div>
          ) : null}
          <div className="mt-1 text-[9px]" style={{ color: '#6F8CC7' }}>
            {brouillon
              ? 'Facture brouillon — non émise'
              : `${typePiece} n° ${f.numero}`}
            {siteWeb ? ` · ${siteWeb}` : ''}
          </div>
        </footer>
      </div>
    </div>
  );
}
